import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, X, SpinnerGap, ClockCounterClockwise } from "@phosphor-icons/react";

export default function GlobalSearch() {
  const STATUS_CLS = { "In stock": "bg-emerald-50 text-emerald-700", "Partly used": "bg-amber-50 text-amber-700", "Fully consumed": "bg-muted text-muted-foreground" };
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState(() => { try { return JSON.parse(localStorage.getItem("gs:recents") || "[]"); } catch { return []; } });
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setGroups(data.groups || []);
      } catch { setGroups([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // "/" focuses search (unless typing in a field)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "/" && !["input", "textarea", "select"].includes(tag) && !e.target.isContentEditable) {
        e.preventDefault(); inputRef.current?.focus(); setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = useCallback((route, item) => {
    setOpen(false); setQ("");
    if (item) {
      const next = [{ ...item, route }, ...recents.filter((r) => r.route !== route)].slice(0, 5);
      setRecents(next);
      localStorage.setItem("gs:recents", JSON.stringify(next));
    }
    if (route) navigate(route);
  }, [navigate, recents]);
  const clearRecents = () => { setRecents([]); localStorage.removeItem("gs:recents"); };

  const totalItems = groups.reduce((s, g) => s + g.items.length, 0);
  const showRecents = open && q.trim().length < 2 && recents.length > 0;

  return (
    <div ref={boxRef} className="relative flex-1 max-w-lg hidden md:block" data-testid="global-search">
      <div className="relative group">
        <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search"
          className="w-full h-8 pl-8 pr-8 rounded-md bg-muted/60 border border-input text-foreground placeholder:text-muted-foreground text-sm transition-colors focus:outline-none focus:bg-background focus:border-primary"
          data-testid="global-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); inputRef.current?.focus(); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="global-search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      {showRecents && (
        <div className="absolute left-0 right-0 mt-1 min-w-[420px] bg-card/95 backdrop-blur-md text-foreground border border-border rounded-md shadow-xl z-50" data-testid="global-search-recents">
          <div className="flex items-center justify-between px-3 pt-2 pb-1 bg-muted/40">
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground inline-flex items-center gap-1"><ClockCounterClockwise size={11} /> Recently opened</span>
            <button onClick={clearRecents} className="font-mono text-[10px] text-muted-foreground hover:text-destructive" data-testid="global-search-recents-clear">Clear</button>
          </div>
          {recents.map((r, i) => (
            <button key={r.route} onClick={() => go(r.route, r)} className="w-full text-left px-3 py-2 hover:bg-primary/10 flex items-baseline gap-2" data-testid={`global-search-recent-${i}`}>
              <span className="font-mono text-[12px] font-semibold text-primary shrink-0">{r.title || "—"}</span>
              {r.subtitle && <span className="text-[12px] text-muted-foreground truncate">{r.subtitle}</span>}
              <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70 shrink-0">{r.label}</span>
            </button>
          ))}
        </div>
      )}

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-1 min-w-[420px] bg-card/95 backdrop-blur-md text-foreground border border-border rounded-md shadow-xl max-h-[70vh] overflow-y-auto z-50" data-testid="global-search-results">
          {loading && totalItems === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 font-mono text-xs text-muted-foreground"><SpinnerGap size={14} className="animate-spin" /> Searching…</div>
          ) : totalItems === 0 ? (
            <div className="px-3 py-4 font-mono text-xs text-muted-foreground">No results in your accessible modules</div>
          ) : (
            groups.map((g) => (
              <div key={g.type} className="border-b border-border last:border-b-0">
                <div className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground bg-muted/40">{g.label}</div>
                {g.items.map((it, i) => (
                  <button
                    key={`${g.type}-${i}`}
                    onClick={() => go(it.route, { title: it.title, subtitle: it.subtitle, label: g.label })}
                    className="w-full text-left px-3 py-2 hover:bg-primary/10 flex items-baseline gap-2 flex-wrap"
                    data-testid={`global-search-item-${g.type}-${i}`}
                  >
                    <span className="font-mono text-[12px] font-semibold text-primary shrink-0">{it.title || "—"}</span>
                    {it.subtitle && <span className="text-[12px] text-muted-foreground whitespace-normal break-words">{it.subtitle}</span>}
                    {it.status && <span className={`ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${STATUS_CLS[it.status] || "bg-muted text-muted-foreground"}`} data-testid={`global-search-item-${g.type}-${i}-status`}>{it.status}</span>}
                    {it.meta && <span className="basis-full font-mono text-[10px] text-muted-foreground" data-testid={`global-search-item-${g.type}-${i}-meta`}>{it.meta}</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
